import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, CalendarOff, Settings2, RotateCcw, AlertTriangle } from "lucide-react";

const TIPO_LABELS = { ferie: "Ferie", malattia: "Malattia", permesso: "Permesso", altro: "Altro" };
const STATO_LABELS = { in_corso: { l: "In corso", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" }, futura: { l: "Futura", cls: "bg-lavender-100 text-lavender-700 dark:bg-lavender-900/40 dark:text-lavender-300" }, passata: { l: "Passata", cls: "bg-muted text-muted-foreground" } };

// v103 — Preset helper
const _todayIso = () => new Date().toISOString().slice(0, 10);
const _addDaysIso = (iso, n) => { const d = new Date(iso); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const _lastWeekRange = () => {
  const d = new Date(); const dow = d.getDay() || 7; // 1=lun ... 7=dom
  const lastMondayDate = new Date(d); lastMondayDate.setDate(d.getDate() - (dow - 1) - 7);
  const lastSundayDate = new Date(lastMondayDate); lastSundayDate.setDate(lastMondayDate.getDate() + 6);
  return { from: lastMondayDate.toISOString().slice(0, 10), to: lastSundayDate.toISOString().slice(0, 10) };
};
const _lastMonthRange = () => {
  const d = new Date();
  const firstThis = new Date(d.getFullYear(), d.getMonth(), 1);
  const lastPrev = new Date(firstThis); lastPrev.setDate(firstThis.getDate() - 1);
  const firstPrev = new Date(lastPrev.getFullYear(), lastPrev.getMonth(), 1);
  return { from: firstPrev.toISOString().slice(0, 10), to: lastPrev.toISOString().slice(0, 10) };
};

export default function AbsencesDialog({ user, open, onOpenChange }) {
  const [tab, setTab] = useState("list"); // list | reset
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ tipo: "ferie", data_inizio: "", data_fine: "", nota: "" });
  const [resetForm, setResetForm] = useState({ data_inizio: "", data_fine: "", includi_non_eseguite: true });

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${user.id}/absences`);
      setAbsences(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setTab("list"); load(); } }, [open, user?.id]);

  const create = async () => {
    if (!form.data_inizio || !form.data_fine) return toast.error("Date obbligatorie");
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${user.id}/absences`, form);
      toast.success(`Assenza creata · ${data.tasks_cancelled || 0} task camere annullate`);
      setShowCreate(false);
      setForm({ tipo: "ferie", data_inizio: "", data_fine: "", nota: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa assenza? Le task già annullate NON verranno ripristinate.")) return;
    try { await api.delete(`/users/${user.id}/absences/${id}`); toast.success("Assenza eliminata"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReset = async () => {
    if (!resetForm.data_inizio || !resetForm.data_fine) return toast.error("Date obbligatorie");
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${user.id}/tasks/reset`, resetForm);
      toast.success(`${data.reset || 0} task contrassegnate come reset admin`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!user) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="absences-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <CalendarOff className="h-4 w-4 text-lavender-600" />
            Assenze · {user.nome} {user.cognome}
          </DialogTitle>
          <DialogDescription>Ferie · Malattia · Permesso · Altro. Le assenze annullano le task <strong>camere</strong> negli hotel del RM nel periodo.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5 mb-2">
          <button onClick={() => setTab("list")} data-testid="tab-list" className={`px-3 h-8 rounded-full text-xs font-medium ${tab === "list" ? "bg-lavender-500 text-white" : "border border-border"}`}>
            <CalendarOff className="h-3 w-3 inline mr-1" /> Assenze
          </button>
          <button onClick={() => setTab("reset")} data-testid="tab-reset" className={`px-3 h-8 rounded-full text-xs font-medium ${tab === "reset" ? "bg-lavender-500 text-white" : "border border-border"}`}>
            <RotateCcw className="h-3 w-3 inline mr-1" /> Reset task
          </button>
        </div>

        {tab === "list" && (
          <div className="space-y-3">
            <div className="flex items-center justify-end">
              <Button onClick={() => setShowCreate(true)} size="sm" className="gap-1.5" data-testid="add-absence-btn">
                <Plus className="h-4 w-4" /> Nuova assenza
              </Button>
            </div>
            {loading ? <div className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></div> : (
              <div className="space-y-2">
                {absences.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessuna assenza registrata</p>}
                {absences.map((a) => {
                  const st = STATO_LABELS[a.stato_periodo] || STATO_LABELS.passata;
                  return (
                    <div key={a.id} className="rounded-xl border border-border/60 bg-background/40 p-3 flex items-start gap-3" data-testid={`absence-row-${a.id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{TIPO_LABELS[a.tipo] || a.tipo}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.l}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          dal <span className="text-foreground tabular-nums">{a.data_inizio}</span> al <span className="text-foreground tabular-nums">{a.data_fine}</span>
                        </div>
                        {a.nota && <div className="text-xs text-muted-foreground mt-1 italic">"{a.nota}"</div>}
                      </div>
                      <button onClick={() => del(a.id)} className="text-rose-500 hover:text-rose-600 p-1" data-testid={`delete-absence-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === "reset" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Marca come <strong>resettata_admin</strong> le task in ritardo nel range. Non conta nei KPI/penalità. Usa con cautela: non ripristinabile.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Da</label>
                <Input type="date" value={resetForm.data_inizio} onChange={(e) => setResetForm({ ...resetForm, data_inizio: e.target.value })} data-testid="reset-from" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">A</label>
                <Input type="date" value={resetForm.data_fine} onChange={(e) => setResetForm({ ...resetForm, data_fine: e.target.value })} data-testid="reset-to" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={resetForm.includi_non_eseguite} onChange={(e) => setResetForm({ ...resetForm, includi_non_eseguite: e.target.checked })} data-testid="reset-include-noneseg" />
              Includi anche task in stato "non eseguita"
            </label>
            <Button onClick={doReset} disabled={saving} className="w-full" data-testid="reset-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />} Reset task in ritardo
            </Button>
          </div>
        )}

        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="sm:max-w-md" data-testid="add-absence-modal">
            <DialogHeader><DialogTitle>Nuova assenza</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Tipo</label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="absence-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPO_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Da</label>
                  <Input type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} data-testid="absence-from" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">A</label>
                  <Input type="date" value={form.data_fine} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} data-testid="absence-to" />
                </div>
              </div>
              {/* v103 — Preset retroattivi */}
              <div className="flex flex-wrap gap-1.5">
                <button type="button" data-testid="vacation-preset-last-week"
                  onClick={() => { const r = _lastWeekRange(); setForm((f) => ({ ...f, data_inizio: r.from, data_fine: r.to })); }}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors">
                  Settimana scorsa
                </button>
                <button type="button" data-testid="vacation-preset-last-month"
                  onClick={() => { const r = _lastMonthRange(); setForm((f) => ({ ...f, data_inizio: r.from, data_fine: r.to })); }}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors">
                  Mese scorso
                </button>
                <button type="button"
                  onClick={() => setForm((f) => ({ ...f, data_inizio: _todayIso(), data_fine: _todayIso() }))}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors">
                  Oggi
                </button>
                <button type="button"
                  onClick={() => setForm((f) => ({ ...f, data_inizio: _todayIso(), data_fine: _addDaysIso(_todayIso(), 6) }))}
                  className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent transition-colors">
                  Prossimi 7gg
                </button>
              </div>
              {form.data_inizio && form.data_inizio < _todayIso() && (
                <div
                  data-testid="vacation-backdate-banner"
                  className="rounded-lg bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200 flex items-start gap-2"
                >
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>
                    ⚠️ Stai registrando un <strong>periodo passato</strong>. Le task già <strong>completate dai colleghi</strong> non verranno toccate; verranno annullate solo le task <strong>pending</strong>.
                  </span>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Nota (opzionale)</label>
                <Textarea value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} className="min-h-[70px]" data-testid="absence-nota" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button onClick={create} disabled={saving} data-testid="absence-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Crea assenza
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
